import { motion } from 'motion/react';
import { Heart, MessageCircle, Share2, Megaphone } from 'lucide-react';
import { FlagImage } from './utils/countryFlags';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { useYapInteractions } from './YapInteractionsContext';
import { formatTimeAgo, formatFirestoreTime } from './utils/timeFormat';
import { FormattedText } from './FormattedText';
import { PollCard } from './ui/PollCard';
import type { Yap } from '../types';

import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useImageViewer } from './ui/ImageViewerProvider';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { extractFirstUrl } from './utils/textParsers';
import { LinkPreview } from './LinkPreview';
import { PressableCard } from './ui/PressableCard';
import { LikeButton } from './ui/LikeButton';

/**
 * Enhanced EmbeddedYap Component
 * 
 * The definitive embedded form used throughout AfroYap to display original yaps within Shouts.
 * This component maintains minimal attribution (username only), shows media, and provides interactive reactions.
 * 
 * Key Features:
 * - Minimal attribution: Shows only username (no full name, timestamp, or location in previews)
 * - Interactive reaction buttons (like, comment, share, shout)
 * - Disabled shout button to prevent recursive shouting
 * - Media display (images)
 * - Consistent styling with reduced contrast to distinguish from parent Shout frame
 * 
 * Used in:
 * - Shout detail screens
 * - Feed views showing shouted yaps
 * - Profile views showing shouted yaps
 * - Shared yap contexts
 */

interface EmbeddedYapProps {
  // Original yap data
  yapId: string;
  username: string;
  authorName: string;
  avatar: string;
  timestamp: string;
  content: string;
  image?: string;
  images?: string[]; // Added multi-image support
  country?: string; // Author's country
  authorCountryFlag?: string;
  showLocation?: boolean; // Whether to show location
  audioUrl?: string;
  duration?: number;
  /**
   * Suppress this quote's own link preview.
   *
   * A quote-shout is one post on screen: its caption embeds a preview, and this
   * quoted yap was embedding a second one underneath, so a shout whose caption
   * linked somewhere and which quoted a yap that also linked somewhere showed
   * two stacked previews. The parent knows whether it already rendered one, so
   * it decides.
   */
  suppressLinkPreview?: boolean;
  /** Poll on the quoted yap, rendered read-only. Voting belongs on the yap
   *  itself, not inside a quote of it. */
  poll?: Yap['poll'];

  // Engagement data (from original yap)
  likes: number;
  comments: number;
  shouts: number;

  // Interaction handlers
  onYapClick: (e?: React.MouseEvent) => void;
  /** Optional: navigate to the embedded yap's ORIGINAL author. Without this the
   *  handle renders as inert text, which left the original author unreachable
   *  from anywhere a shout is displayed. */
  onAuthorClick?: (e: React.MouseEvent) => void;
  onLikeClick?: (e: React.MouseEvent) => void;
  onCommentClick?: (e: React.MouseEvent) => void;
  onShareClick?: (e: React.MouseEvent) => void;

  // Flags
  isLiked?: boolean;
  isShout?: boolean; // If true, this embedded yap is itself a shout (prevents recursive shouting)
  minimal?: boolean; // If true, show only username and content (for shouts without caption)
  isDeleted?: boolean; // Explicitly mark as deleted (overrides content check)

  // Styling
  maxLines?: number;
  className?: string;
  showActions?: boolean; // Whether to show action buttons (default: true)
  small?: boolean; // 🖼️ Added for compact display (e.g. DMs)
  isInteractionAllowed?: boolean;
  interactionErrorMessage?: string;
}

export function EmbeddedYap({
  yapId,
  username,
  authorName,
  avatar,
  onAuthorClick,
  timestamp,
  content,
  image,
  images, // Deconstruct images
  country,
  authorCountryFlag,
  showLocation = true,
  audioUrl,
  duration,
  poll,
  likes,
  comments,
  shouts,
  onYapClick,
  onLikeClick,
  onCommentClick,
  onShareClick,
  isLiked = false,
  isShout = false,
  minimal = false,
  maxLines = 3,
  className = '',
  showActions = true,
  isDeleted = false,
  small = false, // 🖼️ Default to false
  isInteractionAllowed = true,
  interactionErrorMessage = "Members only",
  suppressLinkPreview,
}: EmbeddedYapProps) {
  const { getLikeCount, getShoutCount, getPostReplyCount } = useYapInteractions();
  const { open } = useImageViewer();

  // Detect deleted yap — either via explicit prop or the backend sentinel string
  const isActuallyDeleted = isDeleted || content === 'This yap has been deleted';

  if (isActuallyDeleted) {
    return (
      <div
        className={`px-3 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-black/5 dark:bg-[#080808] flex items-center gap-3 ${className}`}
      >
        <div className="w-8 h-8 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center shrink-0">
          <svg className="w-4 h-4 text-[#999999] dark:text-[#666666]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>
        <p className="text-xs text-[#999999] dark:text-[#666666] italic">
          This yap has been deleted.
        </p>
      </div>
    );
  }

  return (
    <PressableCard
      onClick={(e) => {
        e.stopPropagation();
        onYapClick(e);
      }}
      className={`${small ? 'px-2 py-1' : 'px-3 py-1.5'} rounded-xl cursor-pointer transition-colors flex flex-col ${
        className.includes('bg-transparent') 
          ? '' 
          : 'bg-black/5 dark:bg-[#080808] hover:bg-black/10 dark:hover:bg-[#101010]'
      } ${
        className.includes('border-none')
          ? ''
          : 'border border-black/10 dark:border-white/10'
      } ${className}`}
    >
      {/* Header - Attribution (standardized vs full) */}
      {!minimal ? (
        <div className={`flex items-center gap-1.5 ${small ? 'mb-0' : 'mb-0.5'} shrink-0`}>
          <span
            className={`${small ? 'text-[10px]' : 'text-xs'} text-[#666666] dark:text-[#a0a0a0] truncate opacity-80 ${onAuthorClick ? 'cursor-pointer hover:underline' : ''}`}
            onClick={onAuthorClick ? (e) => { e.stopPropagation(); onAuthorClick(e); } : undefined}
          >
            {`@${(username || '').replace(/@/g, '') || 'user'}`}
          </span>

          {timestamp && (
            <>
              <span className="opacity-30 shrink-0 text-[#666666] dark:text-[#a0a0a0]">•</span>
              <span className={`shrink-0 ${small ? 'text-[10px]' : 'text-xs'} text-[#666666] dark:text-[#a0a0a0] opacity-80`}>{formatFirestoreTime(timestamp)}</span>
            </>
          )}
        </div>
      ) : (
          <div className="flex flex-col min-w-0">
            <div className={`flex items-center gap-1.5 ${small ? 'mb-0' : 'overflow-hidden'}`}>
              <span
                className={`${small ? 'text-[10px]' : 'text-xs'} text-[#666666] dark:text-[#a0a0a0] truncate opacity-80 ${onAuthorClick ? 'cursor-pointer hover:underline' : ''}`}
                onClick={onAuthorClick ? (e) => { e.stopPropagation(); onAuthorClick(e); } : undefined}
              >
                {`@${(username || '').replace(/@/g, '') || 'user'}`}
              </span>

              {timestamp && (
                <>
                  <span className="opacity-30 shrink-0 text-[#666666] dark:text-[#a0a0a0]">•</span>
                  <span className={`shrink-0 ${small ? 'text-[10px]' : 'text-xs'} text-[#666666] dark:text-[#a0a0a0] opacity-80`}>{formatFirestoreTime(timestamp)}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Content Section - Tight centering */}
        <div className={`flex-1 flex flex-col justify-center ${small ? 'py-0.5' : 'py-0.5'} gap-1`}>
          {/* Content */}
          <FormattedText 
            text={content}
            className={`${small ? 'text-[12px]' : 'text-[13px]'} text-[#373737] dark:text-[#E5E5E5] whitespace-pre-wrap wrap-break-word block font-medium ${maxLines === 1 ? 'line-clamp-1' :
              maxLines === 2 ? 'line-clamp-2' :
                maxLines === 3 ? 'line-clamp-3' :
                  maxLines === 4 ? 'line-clamp-4' : ''
              }`}
          />
          
          {(() => {
            if (suppressLinkPreview) return null;
            const url = extractFirstUrl(content);
            return url ? <LinkPreview url={url} /> : null;
          })()}

          {/* Read-only: this is a quote of the yap, not the yap. Voting belongs
              on the post itself, so tapping through is the path to that. */}
          {poll?.options?.length ? (
            <PollCard yapId={String(yapId)} poll={poll} readOnly />
          ) : null}

          {audioUrl && (
            <div className="mt-1">
              <VoiceReplyBubble
                audioUrl={audioUrl} 
                duration={duration} 
                className={`w-full ${small ? 'scale-[0.8]' : 'scale-90'} origin-left`}
              />
            </div>
          )}

          {/* Images — open the shared app-level viewer (immune to feed re-renders,
              supports prev/next) with the whole embed's image array + tapped index */}
          {(() => {
            const embImgs = (images && images.length > 0 ? images : (image ? [image] : [])).filter(Boolean) as string[];
            if (embImgs.length === 0) return null;
            return (
              <div className={`mt-1 gap-2 ${embImgs.length >= 2 ? 'grid grid-cols-2' : 'grid grid-cols-1'}`}>
                {embImgs.map((img, idx) => (
                  <div key={idx} className="rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
                    <img
                      src={img}
                      alt={`Embedded content ${idx + 1}`}
                      loading="lazy"
                      onClick={(e) => { e.stopPropagation(); open(embImgs, idx); }}
                      className={`w-full h-auto object-contain cursor-zoom-in ${embImgs.length === 1 ? (small ? 'max-h-[150px]' : 'max-h-[300px]') : ''}`}
                    />
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Interactive Action Buttons */}
        {showActions && (
          <div className="flex items-center gap-3 pt-1 shrink-0">
            {/* Like Button */}
            <LikeButton
              postId={yapId}
              postType="yap"
              originalLikeCount={likes}
              canInteract={() => isInteractionAllowed}
              onBlockedInteraction={() => {
                toast.error(interactionErrorMessage);
              }}
              isLikedOverride={isLiked}
              onToggleOverride={onLikeClick}
              displayCountOverride={getLikeCount(likes, yapId)}
            />

            {/* Comment Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onCommentClick?.(e);
              }}
              className="flex items-center gap-1.5 h-auto px-2 py-1 text-[#373737] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs">{getPostReplyCount(comments, yapId)}</span>
            </Button>

            {/* Share Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onShareClick?.(e);
              }}
              className="flex items-center gap-1.5 h-auto px-2 py-1 text-[#373737] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]"
            >
              <Share2 className="w-4 h-4" />
            </Button>

            {/* Shout Button - Disabled if this is itself a shout (prevents recursive shouting) */}
            <Button
              variant="ghost"
              size="sm"
              disabled={isShout}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                // This would open shout modal, but disabled for recursive shouting
              }}
              className={`flex items-center gap-1.5 h-auto px-2 py-1 ${isShout
                ? 'text-[#999999] dark:text-[#666666] cursor-not-allowed opacity-50'
                : 'text-[#373737] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]'
                }`}
              title={isShout ? "Can't shout a shout" : "Shout this yap"}
            >
              <Megaphone className="w-4 h-4" />
              <span className="text-xs">{getShoutCount(shouts, yapId)}</span>
            </Button>
          </div>
        )}
    </PressableCard>
  );
}
